import React, { useCallback, useEffect, useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Switch,
  Tag,
  message,
} from 'antd';
import { CheckCircleOutlined, KeyOutlined, SaveOutlined, SyncOutlined } from '@ant-design/icons';
import { knowledgeRagApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const FALLBACK_DEFAULTS = {
  gapgpt_api_url: 'https://api.gapgpt.app/v1',
  embedding_model: 'text-embedding-3-small',
  chat_model: 'gapgpt-qwen-3.5',
};

const EMBEDDING_MODEL_OPTIONS = [
  { value: 'text-embedding-3-small', label: 'text-embedding-3-small' },
  { value: 'text-embedding-3-large', label: 'text-embedding-3-large' },
];

const CHAT_MODEL_OPTIONS = [{ value: 'gapgpt-qwen-3.5', label: 'gapgpt-qwen-3.5' }];

const formatDateTime = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString('fa-IR');
  } catch {
    return value;
  }
};

/**
 * تنظیمات دستیار هوشمند پایگاه دانش (فقط مدیر). موارد فنی در «تنظیمات پیشرفته» جمع شده‌اند.
 * به‌روزرسانی دستیار دستی است؛ وضعیت و اجرای آن از صفحه والد می‌آید.
 */
const KnowledgeRagSettings = ({ status, onRefreshStatus, onRunEmbedding, embeddingRunning = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settingsMeta, setSettingsMeta] = useState(null);

  const clearKey = Form.useWatch('clear_gapgpt_api_key', form);
  const defaults = settingsMeta?.defaults || FALLBACK_DEFAULTS;
  const keyIsSet = Boolean(settingsMeta?.gapgpt_api_key_set);
  const usesCallCenterKey = Boolean(settingsMeta?.uses_callcenter_api_key);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await knowledgeRagApi.getSettings();
      const data = res?.data ?? {};
      setSettingsMeta(data);
      form.setFieldsValue({
        rag_enabled: data.rag_enabled ?? true,
        gapgpt_api_key: '',
        clear_gapgpt_api_key: false,
        gapgpt_api_url: data.gapgpt_api_url || '',
        embedding_model: data.embedding_model || FALLBACK_DEFAULTS.embedding_model,
        chat_model: data.chat_model || FALLBACK_DEFAULTS.chat_model,
        chat_system_prompt: data.chat_system_prompt || '',
        chat_temperature: data.chat_temperature ?? 0.3,
        chat_max_tokens: data.chat_max_tokens ?? 800,
        rag_top_k: data.rag_top_k ?? 5,
        rag_min_score: data.rag_min_score ?? 0.25,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت تنظیمات');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      rag_enabled: Boolean(values.rag_enabled),
      gapgpt_api_url: values.gapgpt_api_url?.trim() || null,
      embedding_model: values.embedding_model?.trim() || null,
      chat_model: values.chat_model?.trim() || null,
      chat_system_prompt: values.chat_system_prompt?.trim() || null,
      chat_temperature: values.chat_temperature,
      chat_max_tokens: values.chat_max_tokens,
      rag_top_k: values.rag_top_k,
      rag_min_score: values.rag_min_score,
    };
    if (values.clear_gapgpt_api_key) {
      payload.clear_gapgpt_api_key = true;
    } else if (values.gapgpt_api_key?.trim()) {
      payload.gapgpt_api_key = values.gapgpt_api_key.trim();
    }

    setSaving(true);
    try {
      const res = await knowledgeRagApi.updateSettings(payload);
      message.success(res?.message || 'تنظیمات ذخیره شد.');
      await fetchSettings();
      onRefreshStatus?.();
    } catch (error) {
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const values = form.getFieldsValue(['gapgpt_api_key', 'gapgpt_api_url', 'embedding_model', 'chat_model']);
      const payload = {};
      Object.entries(values).forEach(([key, value]) => {
        if (value?.trim()) payload[key] = value.trim();
      });
      await knowledgeRagApi.testConnection(payload);
      message.success('اتصال برقرار است.');
    } catch (error) {
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'اتصال برقرار نشد. کلید را بررسی کنید.');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={4} columns={2} />;
  }

  const lastRun = formatDateTime(status?.last_synced_at);
  const pendingChanges = (status?.pending_parts || 0) + (status?.removed_parts || 0);

  return (
    <Form form={form} layout="vertical" className="custom-font pt-2 max-w-3xl">
      <Card size="small" className="shadow-xs mb-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">دستیار هوشمند</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">پاسخ به سوالات بر اساس دانش‌های ثبت‌شده</div>
          </div>
          <Form.Item name="rag_enabled" valuePropName="checked" className="m-0 shrink-0">
            <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
          </Form.Item>
        </div>

        {status?.rag_ready && (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="text-xs text-gray-600 dark:text-gray-300">
              {status.needs_embedding ? (
                <Tag color="processing" className="m-0">{pendingChanges} تغییر جدید</Tag>
              ) : (
                <Tag color="success" icon={<CheckCircleOutlined />} className="m-0">به‌روز</Tag>
              )}
              {lastRun && <span className="text-gray-400 mr-2">آخرین به‌روزرسانی: {lastRun}</span>}
            </div>
            <Popconfirm
              title="دستیار با آخرین تغییرات به‌روز شود؟"
              okText="به‌روزرسانی"
              cancelText="انصراف"
              onConfirm={onRunEmbedding}
              disabled={!status.total_parts}
            >
              <Button size="small" icon={<SyncOutlined />} loading={embeddingRunning} disabled={!status.total_parts}>
                به‌روزرسانی دستیار
              </Button>
            </Popconfirm>
          </div>
        )}
      </Card>

      <Card size="small" className="shadow-xs mb-4">
        <Form.Item
          label="کلید API (GapGPT)"
          name="gapgpt_api_key"
          extra={keyIsSet
            ? 'کلید ثبت شده است. برای تغییر، کلید جدید وارد کنید.'
            : usesCallCenterKey
              ? 'از کلید مرکز تماس استفاده می‌شود.'
              : 'خالی بگذارید تا از کلید مرکز تماس استفاده شود.'}
        >
          <Input.Password
            placeholder={keyIsSet ? '••••••••••••' : 'sk-...'}
            dir="ltr"
            autoComplete="new-password"
            disabled={clearKey}
            prefix={<KeyOutlined className="text-gray-400" />}
          />
        </Form.Item>
        {keyIsSet && (
          <Form.Item name="clear_gapgpt_api_key" valuePropName="checked" className="-mt-3">
            <Checkbox className="text-xs">حذف کلید</Checkbox>
          </Form.Item>
        )}
        <Button size="small" onClick={handleTest} loading={testing}>
          تست اتصال
        </Button>
      </Card>

      <Collapse
        className="mb-4"
        items={[{
          key: 'advanced',
          label: <span className="text-sm">تنظیمات پیشرفته</span>,
          forceRender: true,
          children: (
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="آدرس API" name="gapgpt_api_url" rules={[{ type: 'url', message: 'آدرس معتبر وارد کنید' }]}>
                  <Input placeholder={defaults.gapgpt_api_url} dir="ltr" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="مدل پاسخ‌گویی" name="chat_model">
                  <AutoComplete options={CHAT_MODEL_OPTIONS} placeholder={defaults.chat_model} dir="ltr" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="مدل جستجو" name="embedding_model" extra="پس از تغییر، دستیار را به‌روزرسانی کنید.">
                  <AutoComplete options={EMBEDDING_MODEL_OPTIONS} placeholder={defaults.embedding_model} dir="ltr" />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="تعداد منابع هر پاسخ" name="rag_top_k">
                  <InputNumber min={1} max={20} className="w-full" />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="حداقل میزان ارتباط" name="rag_min_score">
                  <InputNumber min={0} max={1} step={0.05} className="w-full" />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="خلاقیت پاسخ" name="chat_temperature">
                  <InputNumber min={0} max={2} step={0.1} className="w-full" />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="حداکثر طول پاسخ" name="chat_max_tokens">
                  <InputNumber min={64} max={8000} step={50} className="w-full" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="دستورالعمل دستیار" name="chat_system_prompt" extra="خالی = پیش‌فرض">
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    placeholder={defaults.chat_system_prompt}
                    maxLength={20000}
                  />
                </Form.Item>
              </Col>
            </Row>
          ),
        }]}
      />

      <div className="flex justify-end">
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          className={PANEL_PRIMARY_BTN_CLASS}
        >
          ذخیره
        </Button>
      </div>
    </Form>
  );
};

export default KnowledgeRagSettings;
